import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuthenticator } from '@aws-amplify/ui-react';
import useGetCurrentUser from '../../API/hooks/users/useGetCurrentUser';
import useGetVideoById from '../../API/hooks/videos/useGetVideoById';

import useGetDeviceLocations from '../../API/hooks/maps/useGetDeviceLocations';
import useGetAlertById from '../../API/hooks/alerts/useGetAlertById';
import useGetDeviceByDeviceId from '../../API/hooks/devices/useGetDeviceByDeviceId';
import useGetDriverById from '../../API/hooks/drivers/useGetDriverById';
import useGetVehicleById from '../../API/hooks/vehicles/useGetVehicleById';
import useDeleteVideo from '../../API/hooks/videos/useDeleteVideo';
import useGetRole from '../../API/hooks/useGetRole';
import Icon from '../../components/common/Icon/Icon';
import { IconKeys } from '../../components/common/Icon/IconKeys';
import Tags from '../../components/common/Tags';
import { Button } from '../../components/ui/Button/Button';
import HeaderWrapper from '../../components/ui/DetailsPageHeaderWrapper/HeaderWrapper';
import MapWidget from '../../components/common/Map/Map';
import SingleVideo from '../../components/VideoContainer/SingleVideo/SingleVideo';
import { getFormattedDateAndTime, formatDurationFromSeconds, formatDurationFromRange } from '../../utils/dateUtils';
import { getFulfillmentDetailMessage, getFulfillmentStatusLabel } from '../../utils/videoFulfillmentUtils';
import {
  buildDeleteVideoRequest,
  canShowVideoDeleteAction,
  isActiveCustomVodVideo,
} from '../../utils/videoDeleteUtils';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import useNoThumbnail from '../../theme/useNoThumbnail';
import S from './VideoDetailsStyles';
import Modal from '../../components/common/Modal/Modal';
import CircularProgress from '../../components/common/CircularProgress/CircularProgress';
import DeleteModal from '../../components/common/DeleteModal/DeleteModal';
import Notes from '../../components/Notes/Notes';
import CreateNote from '../../components/Notes/CreateNote/CreateNote';
import DetailsPanel from '../../components/DetailsPanel/DetailsPanel';

const videoStatusNames: Record<string, string> = {
  completed: 'Video present',
  startedProcessing: 'Uploading',
  pending: 'Requesting video',
  failedToRequest: 'confirming video availability',
  failedToUpload: 'confirming video availability',
  failedFinally: 'Video is not available on the device',
  failedAttemptsLimitExceeded: 'video could not be downloaded due to connection issue',
  failedDeviceRemovedFromFleet: 'Video unavailable - device was removed from the fleet',
};

const cameraType = [
  'Multiple cameras',
  'Outward camera',
  'Inward camera',
];

// Map is temporarily hidden on this page - not used yet and confusing users.
// Flip to true to restore it once map functionality is implemented here.
const SHOW_MAP = false;

function renderUploadProgressContent(video: any): JSX.Element {
  if (video.status !== 'startedProcessing') {
    return <span>{videoStatusNames[video.status]}</span>;
  }
  if (typeof video?.uploadPercentage === 'number') {
    return (
      <CircularProgress
        progress={video.uploadPercentage}
        labelText="Uploading video..."
      />
    );
  }
  return <span>Waiting for the device to start upload…</span>;
}

function VideoDetailsPage(): JSX.Element {
  const noThumbnail = useNoThumbnail();
  const fleetId = useGetFleetId();
  const { user } = useAuthenticator();
  const { data: currentUser } = useGetCurrentUser(fleetId, user.attributes?.email);
  const navigate = useNavigate();
  const { id: videoId } = useParams();
  const { isFleetViewer } = useGetRole();
  const [addNoteModalOpen, toggleAddNoteModalOpen] = useState(false);
  const [deleteModalOpen, toggleDeleteModalOpen] = useState(false);
  const [allowedToFetch, setAllowedToFetch] = useState(true);
  const [measuredDuration, setMeasuredDuration] = useState<number | null>(null);

  const { data: video, isLoading, error } = useGetVideoById(
    videoId as string,
    fleetId,
    allowedToFetch,
  );
  const { data: device } = useGetDeviceByDeviceId(video?.device?.deviceId as string, fleetId);
  const { data: driver } = useGetDriverById(video?.driver?.driverId as string, fleetId);
  const { data: vehicle } = useGetVehicleById(video?.vehicle?.vehicleId as string, fleetId);
  const { data: alert, isSuccess: alertsLoaded } = useGetAlertById(video?.alertId as string, fleetId);
  const thumbnail = video?.thumbnailUrls[0];
  const alertDate = video?.fromTimestamp || video?.alertTimestamp;
  const duration = measuredDuration != null
    ? formatDurationFromSeconds(measuredDuration)
    : formatDurationFromRange(video?.fromTimestamp, video?.toTimestamp);
  const handleBackToList = (): void => navigate('/videos/');

  const handleGoBack = (): void => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      handleBackToList();
    }
  };

  const { mutate: mutateDelete, isLoading: isDeleteLoading } = useDeleteVideo(
    () => {
      toggleDeleteModalOpen(false);
      handleBackToList();
    },
    (errorMessage) => {
      toggleDeleteModalOpen(false);
      toast.error(errorMessage);
    },
  );

  useEffect(() => {
    if (video?.url) {
      setAllowedToFetch(false);
    }
  }, [video]);

  useEffect(() => {
    setMeasuredDuration(null);
  }, [video?._id]);

  const handleAddNote = (): void => {
    toggleAddNoteModalOpen(true);
  };

  const handleDeleteClick = (): void => {
    toggleDeleteModalOpen(true);
  };

  const handleDeleteVideo = (): void => {
    if (!video) {
      return;
    }

    mutateDelete(buildDeleteVideoRequest(fleetId, video, currentUser?._id));
  };

  const isActiveCustomVod = video ? isActiveCustomVodVideo(video) : false;
  const showDeleteAction = video
    ? canShowVideoDeleteAction(video, currentUser?._id, isFleetViewer)
    : false;
  const deleteActionTitle = isActiveCustomVod ? 'Cancel request' : 'Delete video';
  const deleteModalTitle = isActiveCustomVod
    ? 'Are you sure you want to cancel this VoD request?'
    : 'Are you sure you want to delete this video?';

  const fulfillmentDetail = video ? getFulfillmentDetailMessage(video) : null;
  const fulfillmentLabel = video
    ? getFulfillmentStatusLabel(video.fulfillmentStatus, videoStatusNames[video.status], video.status)
    : '';

  if (error) {
    handleBackToList();
  }
  return (
    <>
      {isLoading && (<div>Loading...</div>)}

      {!!video && (
        <S.Wrapper>
          <HeaderWrapper>
            <div className="details-row">
              <div className="overview-container">
                <div className="preview-container">
                  <Button height="44px" padding="10px 12px" onClick={handleGoBack}>
                    <Icon iconKey={IconKeys.ARROW_LEFT} />
                  </Button>
                  <Tags.ItemOverview
                    avatar={thumbnail || noThumbnail}
                    avatarAlt="video"
                    title={video.deviceId}
                    subtitle={fulfillmentLabel || videoStatusNames[video.status]}
                  />
                </div>
              </div>
              <div className="actions-wrapper">
                <div className="border-line" />
                {/* <Button
                height="44px"
                padding="10px 12px"
                title="Add note"
                onClick={handleAddNote}
              >
                <Icon iconKey={IconKeys.ADD_NOTE} />
              </Button> */}
                {showDeleteAction && (
                  <Button
                    height="44px"
                    padding="10px 12px"
                    title={deleteActionTitle}
                    disabled={isDeleteLoading}
                    onClick={handleDeleteClick}
                  >
                    <Icon iconKey={IconKeys.DELETE} />
                  </Button>
                )}
              </div>
            </div>
            <div className="tags-row">
              <Tags.LabeledIcon
                flexible
                label={video.alertId ? 'Alert video' : 'Custom VoD'}
                icon={IconKeys.VIDEO}
              />
              <Tags.LabeledIcon
                flexible
                label={`Video start at ${getFormattedDateAndTime(alertDate ? new Date(alertDate) : '', true)}`}
                icon={IconKeys.TIME}
              />
              {!!duration && (
                <Tags.LabeledIcon
                  flexible
                  label={`Duration: ${duration}`}
                  icon={IconKeys.CLOCK}
                />
              )}
              {!!vehicle && (
                <Tags.LabeledIcon
                  label={vehicle.licensePlateNumber}
                  icon={IconKeys.TRUCK}
                />
              )}
              {!!video.cameraId && (
                <Tags.Label
                  label={cameraType[video.cameraId] || 'Camera type N/A'}
                />
              )}
              <Tags.LabeledIcon
                flexible
                label={video.timeSynchronized === false ? 'Time not synchronized' : 'Time synchronized'}
                icon={video.timeSynchronized === false ? IconKeys.SYNC_FAILED : IconKeys.SYNCED}
              />
              {fulfillmentDetail && (
                <Tags.Label label={fulfillmentDetail} />
              )}
            </div>
          </HeaderWrapper>
          <div className="widgets-container-video">
            <div className="video-container">
              {(video.status === 'startedProcessing' || video?.status === 'pending') && (
                <div className="upload-progress-container">
                  {renderUploadProgressContent(video)}
                </div>
              )}
              {(video.status !== 'startedProcessing' && video?.uploadPercentage === 100) && (
                <SingleVideo
                  url={video.url}
                  hasAudio={video.hasAudio}
                  fillSlot
                  onDurationChange={setMeasuredDuration}
                />
              )}
              {['failedFinally', 'failedDeviceRemovedFromFleet'].includes(video.status) && (
                <div className="upload-progress-container">
                  <span>{videoStatusNames[video.status]}</span>
                </div>
              )}
              {video.status === 'completed' && video?.uploadPercentage !== 100 && !video.url && (
                <div className="no-video-placeholder" />
              )}
              {SHOW_MAP ? (
                <MapWidget
                  isTrip
                  fleetId={fleetId}
                  getLocationsQuery={useGetDeviceLocations}
                  entityId={video?.deviceId as string}
                  entityType="video"
                  startDate={video.from}
                  endDate={video.to}
                  alerts={alert ? [alert] : []}
                  alertsLoaded={alertsLoaded}
                />
              ) : (
                <div className="map-wrapper">
                  <Notes fleetId={fleetId} entityName="videos" entityId={videoId} />
                </div>
              )}
            </div>
          </div>
          <DetailsPanel driver={driver} vehicle={vehicle} device={device} />
        </S.Wrapper>
      )}
      {addNoteModalOpen && videoId && (
        <Modal
          title="Add note for VOD"
          state={addNoteModalOpen}
          toggleState={toggleAddNoteModalOpen}
        >
          <CreateNote fleetId={fleetId} entityName="videos" entityId={videoId} toggleModal={toggleAddNoteModalOpen} />
        </Modal>
      )}
      {deleteModalOpen && (
        <Modal
          title="Confirm delete"
          state={deleteModalOpen}
          toggleState={toggleDeleteModalOpen}
          settings={{
            minWidth: '500px',
          }}
        >
          <DeleteModal
            title={deleteModalTitle}
            onClose={() => toggleDeleteModalOpen(false)}
            onDelete={handleDeleteVideo}
          />
        </Modal>
      )}
    </>
  );
}

export default VideoDetailsPage;
